import { SafeHtml } from "@angular/platform-browser";
import { zDoxItem } from "../../../shared/types";
import { DocumentData } from "../../annotation-controller/document-data.type";
import { Mail } from "./mail-viewer/mail-viewer.type";

/**
 * Key: *ViewerType* — type of viewer\
 * Value: type(s) of content that can be displayed on each viewer
 */
type ViewerTypeToContentTypeMap = {
   text: string;
   document: DocumentData;
   svg: SafeHtml;
   image: string;
   video: string;
   audio: string;
   mail:  MailViewerContent;
   other: CannotPreviewViewerContent;
};

/** 
 * Type of the file/content viewer.
 */
export type ViewerType = keyof ViewerTypeToContentTypeMap;

/**
* The type of a file/content that can be displayed on its corresponding ViewerType.
*
* @template VT - The ViewerType that must be one of the keys from ViewerTypeToContentTypeMap.
*/
export type ViewerContent<VT extends ViewerType> = ViewerTypeToContentTypeMap[VT];

/**
 * Checks if the content type can be displayed by the viewer type. Return true if it can; Compile-error otherwise.
 *
 * The main validation is done by TypeScript's type system at compile-time.
 *
 * This function does NOTHING at runtime.
 *
 * @example
 * const textContent = "This is a sentence";
 * const documentData = {} as DocumentData;
 * validateViewerAndContentType(textContent, "text"); // ✅ Valid
 * validateViewerAndContentType(documentData, "document"); // ✅ Valid
 * validateViewerAndContentType(documentData, "text"); // ❌ TypeScript error
 */
export function validateViewerAndContentType<VT extends ViewerType>(content: ViewerContent<VT>, viewerType: VT): boolean {
   return true;
}

/**
 * Configuration for the file/content viewer.
 * 
 * @property viewerType - type of the viewer
 * @property content - content to be displayed on the viewer
 */
export type FileViewerConfig = {
   viewerType: ViewerType;
   content: ViewerContent<ViewerType>;
}

export type MailViewerContent = {
   mail: Mail;
   doc: zDoxItem;
}

export type CannotPreviewViewerContent = {
   /** 
    * Path to the file-type icon.
    * @example
    * 'assets/icons/file.svg'
    * 'assets/icons/folder.svg'
    * 'assets/icons/image.svg'
    */
   iconPath: string;
   fileName: string;
   fileSizeByte: number;
   /**
    * **true** – show the download button\
    * **false** – hide the download button
    */
   allowDownload: boolean;
}
